import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, HelpCircle, MapPin, Plus, Send } from 'lucide-react'
import { toast } from 'sonner'
import { Alert, AlertDescription, AlertTitle } from '@quebon/ui/components/alert'
import { Badge } from '@quebon/ui/components/badge'
import { Button } from '@quebon/ui/components/button'
import { Checkbox } from '@quebon/ui/components/checkbox'
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@quebon/ui/components/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@quebon/ui/components/dialog'
import { Field, FieldContent, FieldDescription, FieldGroup, FieldLabel } from '@quebon/ui/components/field'
import { Input } from '@quebon/ui/components/input'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@quebon/ui/components/select'
import { Spinner } from '@quebon/ui/components/spinner'
import { Textarea } from '@quebon/ui/components/textarea'
import {
  textbookRequestsApi,
  type AdminTextbookRequestView,
  type HappydayTextbookBatchDispatchBody,
  type TextbookShippingAddress,
  type TextbookShippingAddressInput,
} from '@/api/textbookRequests'
import { handleApiError } from '@/api/client'
import KakaoPostcode from '@/components/common/KakaoPostcode'
import { formatHappydayResponseMessage } from './happydayResponseMessage'
import {
  defaultDispatchSelection,
  groupTextbookRequestsByBook,
  hasHappydayProductMapping,
  summarizeHappydayItems,
  type TextbookRequestBookGroup,
} from './textbookRequestGroups'
import { defaultHappydayOutDate, formatTextbookRequestDate } from './textbookRequestDate'

const emptyAddress: TextbookShippingAddressInput = {
  label: '',
  recipientName: '',
  recipientPhone: '',
  postalCode: '',
  addressLine1: '',
  addressLine2: null,
  deliveryMemo: null,
  isDefault: false,
}

function addressText(address: TextbookShippingAddress) {
  return [address.addressLine1, address.addressLine2].filter(Boolean).join(' ')
}

export function resolveSelectedAddress(
  addresses: TextbookShippingAddress[],
  selectedAddressId: number,
): TextbookShippingAddress | null {
  if (selectedAddressId > 0) {
    const selected = addresses.find((address) => address.id === selectedAddressId)
    if (selected) return selected
  }
  return addresses.find((address) => address.isDefault) ?? addresses[0] ?? null
}

export function isAddressInputComplete(address: TextbookShippingAddressInput) {
  return Boolean(
    address.label.trim()
      && address.recipientName.trim()
      && address.recipientPhone.trim()
      && address.postalCode.trim()
      && address.addressLine1.trim(),
  )
}

export function getHappydayAddressValidationError(
  address: Pick<
    TextbookShippingAddressInput,
    'recipientName' | 'recipientPhone' | 'postalCode' | 'addressLine1' | 'addressLine2'
  >,
): string | null {
  if (address.recipientName.trim().length > 20) return '받는 분은 20자 이내여야 합니다.'
  if (address.recipientPhone.trim().length > 20) return '연락처는 20자 이내여야 합니다.'
  const receiverAddress = [
    address.postalCode.trim() ? `[${address.postalCode.trim()}]` : '',
    address.addressLine1.trim(),
    address.addressLine2?.trim() ?? '',
  ].filter(Boolean).join(' ')
  if (receiverAddress.length > 100) return '우편번호를 포함한 배송 주소는 100자 이내여야 합니다.'
  return null
}

export function normalizeAddressInput(
  address: TextbookShippingAddressInput,
  isFirstAddress: boolean,
): TextbookShippingAddressInput {
  return {
    ...address,
    label: address.label.trim(),
    recipientName: address.recipientName.trim(),
    recipientPhone: address.recipientPhone.trim(),
    postalCode: address.postalCode.trim(),
    addressLine1: address.addressLine1.trim(),
    addressLine2: address.addressLine2?.trim() || null,
    deliveryMemo: address.deliveryMemo?.trim() || null,
    isDefault: isFirstAddress,
  }
}

interface ShippingAddressSelectProps {
  addresses: TextbookShippingAddress[]
  selectedAddressId: number
  onValueChange: (addressId: number) => void
}

export function ShippingAddressSelect({
  addresses,
  selectedAddressId,
  onValueChange,
}: ShippingAddressSelectProps) {
  const items = addresses.map((address) => ({
    value: String(address.id),
    label: `${address.label} · ${address.recipientName}`,
  }))

  return (
    <Select
      items={items}
      value={String(selectedAddressId)}
      onValueChange={(value) => {
        if (value) onValueChange(Number(value))
      }}
    >
      <SelectTrigger id="happyday-address" className="w-full">
        <SelectValue placeholder="배송지를 선택하세요" />
      </SelectTrigger>
      <SelectContent alignItemWithTrigger={false}>
        <SelectGroup>
          {items.map((item) => (
            <SelectItem key={item.value} value={item.value}>
              {item.label}
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  )
}

function RequestLearnerLabel({ request }: { request: AdminTextbookRequestView }) {
  return (
    <span className="font-normal">
      {request.learnerName ?? `학습자 #${request.learnerUserId}`}
      <span className="ml-2 text-xs text-muted-foreground">
        {formatTextbookRequestDate(request.requestedAt)}
      </span>
    </span>
  )
}

interface DispatchRequestChecklistProps {
  groups: TextbookRequestBookGroup[]
  selectedIds: ReadonlySet<number>
  disabled?: boolean
  onToggleRequest: (id: number, checked: boolean) => void
  onToggleBook: (group: TextbookRequestBookGroup, checked: boolean) => void
}

export function DispatchRequestChecklist({
  groups,
  selectedIds,
  disabled = false,
  onToggleRequest,
  onToggleBook,
}: DispatchRequestChecklistProps) {
  return (
    <Field>
      <FieldLabel>출고 대상 선택</FieldLabel>
      <FieldDescription>교재 체크를 끄면 그 교재의 요청이 한 번에 제외됩니다.</FieldDescription>
      <div className="flex flex-col gap-3 rounded-md border p-3">
        {groups.map((group) => {
          const selectedCount = group.requests.filter((item) => selectedIds.has(item.id)).length
          const allSelected = group.mapped && selectedCount === group.requests.length
          const bookCheckboxId = `dispatch-book-${group.printBookId}`
          const single = group.requests.length === 1 ? group.requests[0] : null
          return (
            <div key={group.printBookId} className="flex flex-col gap-2">
              <Field orientation="horizontal">
                <Checkbox
                  id={bookCheckboxId}
                  checked={allSelected}
                  indeterminate={selectedCount > 0 && !allSelected}
                  disabled={disabled || !group.mapped}
                  onCheckedChange={(checked) => onToggleBook(group, checked)}
                />
                <FieldContent>
                  <FieldLabel htmlFor={bookCheckboxId} className="flex flex-wrap items-center gap-2">
                    <span>{group.label}</span>
                    {single ? (
                      <RequestLearnerLabel request={single} />
                    ) : (
                      <Badge variant="secondary">{selectedCount}/{group.requests.length}건</Badge>
                    )}
                    {!group.mapped ? <Badge variant="destructive">상품 매핑 없음</Badge> : null}
                  </FieldLabel>
                </FieldContent>
              </Field>
              {single ? null : (
                <ul className="ml-6 flex flex-col gap-1.5">
                  {group.requests.map((item) => {
                    const requestCheckboxId = `dispatch-request-${item.id}`
                    return (
                      <li key={item.id}>
                        <Field orientation="horizontal">
                          <Checkbox
                            id={requestCheckboxId}
                            checked={selectedIds.has(item.id)}
                            disabled={disabled || !group.mapped}
                            onCheckedChange={(checked) => onToggleRequest(item.id, checked)}
                          />
                          <FieldContent>
                            <FieldLabel htmlFor={requestCheckboxId} className="font-normal">
                              <RequestLearnerLabel request={item} />
                            </FieldLabel>
                          </FieldContent>
                        </Field>
                      </li>
                    )
                  })}
                </ul>
              )}
            </div>
          )
        })}
      </div>
    </Field>
  )
}

interface Props {
  request: AdminTextbookRequestView
  requests: AdminTextbookRequestView[]
  onClose: () => void
  onDispatched: () => void
}

type CenterDispatchForm = Omit<HappydayTextbookBatchDispatchBody, 'requestIds'>

export function HappydayDispatchDialog({ request, requests, onClose, onDispatched }: Props) {
  const queryClient = useQueryClient()
  const addressKey = ['admin', 'textbook-requests', request.id, 'shipping-addresses'] as const
  const [dispatchForm, setDispatchForm] = useState<CenterDispatchForm>({
    attemptId: crypto.randomUUID(),
    outDate: defaultHappydayOutDate(),
    addressId: 0,
    supplyRate: 100,
  })
  const [addingAddress, setAddingAddress] = useState(false)
  const [addressForm, setAddressForm] = useState<TextbookShippingAddressInput>(emptyAddress)
  const [selectedIds, setSelectedIds] = useState<Set<number>>(() => defaultDispatchSelection(requests))

  const addressQuery = useQuery({
    queryKey: addressKey,
    queryFn: () => textbookRequestsApi.listShippingAddresses(request.id),
  })
  const addresses = addressQuery.data ?? []
  const selectedAddress = resolveSelectedAddress(addresses, dispatchForm.addressId)
  const selectedAddressId = selectedAddress?.id ?? 0
  const selectedAddressError = selectedAddress ? getHappydayAddressValidationError(selectedAddress) : null
  const showAddressForm = addingAddress || (addressQuery.isSuccess && addresses.length === 0)
  const bookGroups = groupTextbookRequestsByBook(requests)
  const selectedRequests = requests.filter((item) => selectedIds.has(item.id))
  const itemSummaries = summarizeHappydayItems(selectedRequests)
  const unmappedRequests = requests.filter((item) => !hasHappydayProductMapping(item))
  const missingMapping = unmappedRequests[0]

  const createAddressMutation = useMutation({
    mutationFn: (body: TextbookShippingAddressInput) =>
      textbookRequestsApi.createShippingAddress(request.id, body),
    onSuccess: (address) => {
      queryClient.setQueryData<TextbookShippingAddress[]>(addressKey, (current = []) => [
        address,
        ...current.filter((item) => item.id !== address.id),
      ])
      setDispatchForm((current) => ({ ...current, addressId: address.id }))
      setAddressForm(emptyAddress)
      setAddingAddress(false)
      toast.success('센터 배송지를 등록했습니다.')
    },
    onError: (error) => toast.error(handleApiError(error)),
  })

  const dispatchMutation = useMutation({
    mutationFn: (body: HappydayTextbookBatchDispatchBody) =>
      textbookRequestsApi.dispatchCenterToHappyday(request.organizationId, body),
    onSuccess: (updatedRequests) => {
      onDispatched()
      onClose()
      const updatedRequest = updatedRequests[0]
      if (!updatedRequest) {
        toast.error('처리된 교재 요청을 확인할 수 없습니다.')
        return
      }
      if (updatedRequest.status === 'OUTBOUND_REQUESTED') {
        toast.success(`해피데이에 센터 교재 ${updatedRequests.length}건을 접수했습니다.`)
      } else if (updatedRequest.status === 'OUTBOUND_UNCERTAIN') {
        toast.warning('응답이 불명확합니다. 재시도하지 말고 해피데이 사이트에서 접수 여부를 확인해주세요.')
      } else {
        toast.error(updatedRequest.happydayResponseMessage
          ? formatHappydayResponseMessage(updatedRequest.happydayResponseMessage)
          : '해피데이 출고 요청이 거절되었습니다.')
      }
    },
    onError: (error) => toast.error(handleApiError(error)),
  })

  const canCreateAddress = isAddressInputComplete(addressForm)
    && getHappydayAddressValidationError(addressForm) == null
    && !createAddressMutation.isPending
  const dispatchReady = selectedRequests.length > 0
    && selectedRequests.every(hasHappydayProductMapping)
    && dispatchForm.outDate !== ''
    && selectedAddress != null
    && selectedAddressError == null
    && !dispatchMutation.isPending
  const isBusy = createAddressMutation.isPending || dispatchMutation.isPending

  const updateAddressField = <K extends keyof TextbookShippingAddressInput>(
    key: K,
    value: TextbookShippingAddressInput[K],
  ) => setAddressForm((current) => ({ ...current, [key]: value }))

  const createAddress = () => {
    if (!canCreateAddress) return
    createAddressMutation.mutate(normalizeAddressInput(addressForm, addresses.length === 0))
  }

  const toggleRequest = (id: number, checked: boolean) => {
    setSelectedIds((current) => {
      const next = new Set(current)
      if (checked) next.add(id)
      else next.delete(id)
      return next
    })
  }

  const toggleBook = (group: TextbookRequestBookGroup, checked: boolean) => {
    if (!group.mapped) return
    setSelectedIds((current) => {
      const next = new Set(current)
      for (const item of group.requests) {
        if (checked) next.add(item.id)
        else next.delete(item.id)
      }
      return next
    })
  }

  const submitDispatch = () => {
    if (!dispatchReady) return
    dispatchMutation.mutate({
      ...dispatchForm,
      addressId: selectedAddressId,
      requestIds: selectedRequests.map((item) => item.id),
    })
  }

  return (
    <Dialog open onOpenChange={(open) => { if (!open && !isBusy) onClose() }}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>센터별 해피데이 출고 요청</DialogTitle>
          <DialogDescription>
            {request.organizationName ?? request.organizationSubdomain ?? `센터 #${request.organizationId}`}
            {' · '}교재 요청 {requests.length}건 중 {selectedRequests.length}건 선택
          </DialogDescription>
        </DialogHeader>

        {missingMapping ? (
          <Alert>
            <AlertTriangle />
            <AlertTitle>상품 매핑 없는 교재 {unmappedRequests.length}건은 출고에서 제외됩니다.</AlertTitle>
            <AlertDescription>
              해당 요청은 요청됨 상태로 남고 다음 센터 출고 때 다시 표시됩니다.{' '}
              <Link to={`/contents/books/${missingMapping.printBookId}`}>누락 교재 편집으로 이동</Link>
            </AlertDescription>
          </Alert>
        ) : null}

        <DispatchRequestChecklist
          groups={bookGroups}
          selectedIds={selectedIds}
          disabled={isBusy}
          onToggleRequest={toggleRequest}
          onToggleBook={toggleBook}
        />

        <Alert>
          <AlertTitle>출고 상품 {itemSummaries.length}종 · {selectedRequests.length}건</AlertTitle>
          <AlertDescription className="flex flex-wrap gap-2">
            {itemSummaries.length === 0 ? (
              <span>출고할 요청을 선택해 주세요.</span>
            ) : itemSummaries.map((item) => (
              <Badge key={item.key} variant="secondary">
                {item.label} × {item.quantity}
              </Badge>
            ))}
          </AlertDescription>
        </Alert>

        <FieldGroup>
          <Field>
            <FieldLabel htmlFor="happyday-out-date">출고일</FieldLabel>
            <Input
              id="happyday-out-date"
              type="date"
              value={dispatchForm.outDate}
              onChange={(event) => setDispatchForm((current) => ({ ...current, outDate: event.target.value }))}
            />
            <FieldDescription>16시 이전은 당일, 이후는 다음 영업일이 기본값입니다. 주말·공휴일·해피데이 휴무일은 접수되지 않습니다.</FieldDescription>
          </Field>

          <Field data-invalid={addressQuery.isSuccess && selectedAddress == null}>
            <div className="flex items-center justify-between gap-3">
              <FieldLabel htmlFor="happyday-address">센터 배송지</FieldLabel>
              {!showAddressForm ? (
                <Button type="button" variant="outline" size="sm" onClick={() => setAddingAddress(true)}>
                  <Plus data-icon="inline-start" />
                  새 배송지
                </Button>
              ) : null}
            </div>

            {addressQuery.isLoading ? (
              <div className="flex min-h-20 items-center justify-center">
                <Spinner />
              </div>
            ) : addressQuery.isError ? (
              <Alert variant="destructive">
                <AlertTriangle />
                <AlertTitle>센터 배송지를 불러오지 못했습니다.</AlertTitle>
                <AlertDescription>
                  <Button type="button" variant="outline" size="sm" onClick={() => addressQuery.refetch()}>
                    다시 시도
                  </Button>
                </AlertDescription>
              </Alert>
            ) : addresses.length > 0 ? (
              <>
                <ShippingAddressSelect
                  addresses={addresses}
                  selectedAddressId={selectedAddressId}
                  onValueChange={(addressId) => {
                    setDispatchForm((current) => ({ ...current, addressId }))
                  }}
                />

                {selectedAddress ? (
                  <Card size="sm">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <MapPin />
                        {selectedAddress.label}
                      </CardTitle>
                      <CardDescription>
                        {selectedAddress.recipientName} · {selectedAddress.recipientPhone}
                      </CardDescription>
                      {selectedAddress.isDefault ? (
                        <CardAction><Badge variant="secondary">기본 배송지</Badge></CardAction>
                      ) : null}
                    </CardHeader>
                    <CardContent className="flex flex-col gap-1 text-muted-foreground">
                      <p>[{selectedAddress.postalCode}] {addressText(selectedAddress)}</p>
                      {selectedAddress.deliveryMemo ? <p>배송 메모 · {selectedAddress.deliveryMemo}</p> : null}
                    </CardContent>
                  </Card>
                ) : null}
                {selectedAddressError ? (
                  <Alert variant="destructive">
                    <AlertTriangle />
                    <AlertTitle>해피데이 배송지 규격을 확인해 주세요.</AlertTitle>
                    <AlertDescription>{selectedAddressError} 새 배송지를 등록해 출고할 수 있습니다.</AlertDescription>
                  </Alert>
                ) : null}
              </>
            ) : null}
          </Field>

          {showAddressForm ? (
            <Card size="sm">
              <CardHeader>
                <CardTitle>새 센터 배송지</CardTitle>
                <CardDescription>등록한 배송지는 센터정보와 깨봉몰에서도 함께 사용됩니다.</CardDescription>
              </CardHeader>
              <CardContent>
                <FieldGroup>
                  <Field>
                    <FieldLabel htmlFor="new-address-label">배송지 이름</FieldLabel>
                    <Input
                      id="new-address-label"
                      value={addressForm.label}
                      maxLength={100}
                      placeholder="예: 강남센터"
                      onChange={(event) => updateAddressField('label', event.target.value)}
                    />
                  </Field>
                  <FieldGroup className="grid gap-4 sm:grid-cols-2">
                    <Field>
                      <FieldLabel htmlFor="new-address-recipient">받는 분</FieldLabel>
                      <Input
                        id="new-address-recipient"
                        value={addressForm.recipientName}
                        maxLength={20}
                        onChange={(event) => updateAddressField('recipientName', event.target.value)}
                      />
                    </Field>
                    <Field>
                      <FieldLabel htmlFor="new-address-phone">연락처</FieldLabel>
                      <Input
                        id="new-address-phone"
                        value={addressForm.recipientPhone}
                        maxLength={20}
                        inputMode="tel"
                        onChange={(event) => updateAddressField('recipientPhone', event.target.value)}
                      />
                    </Field>
                  </FieldGroup>
                  <Field>
                    <FieldLabel htmlFor="new-address-postal-code">우편번호</FieldLabel>
                    <div className="flex gap-2">
                      <Input
                        id="new-address-postal-code"
                        value={addressForm.postalCode}
                        maxLength={20}
                        onChange={(event) => updateAddressField('postalCode', event.target.value)}
                      />
                      <KakaoPostcode
                        disabled={createAddressMutation.isPending}
                        onSelect={({ postalCode, addressBase }) => {
                          setAddressForm((current) => ({
                            ...current,
                            postalCode,
                            addressLine1: addressBase,
                          }))
                        }}
                      />
                    </div>
                  </Field>
                  <Field>
                    <FieldLabel htmlFor="new-address-line-1">기본 주소</FieldLabel>
                    <Input
                      id="new-address-line-1"
                      value={addressForm.addressLine1}
                      maxLength={300}
                      onChange={(event) => updateAddressField('addressLine1', event.target.value)}
                    />
                  </Field>
                  <Field>
                    <FieldLabel htmlFor="new-address-line-2">상세 주소</FieldLabel>
                    <Input
                      id="new-address-line-2"
                      value={addressForm.addressLine2 ?? ''}
                      maxLength={300}
                      onChange={(event) => updateAddressField('addressLine2', event.target.value)}
                    />
                  </Field>
                  <Field>
                    <FieldLabel htmlFor="new-address-memo">배송 메모</FieldLabel>
                    <Textarea
                      id="new-address-memo"
                      value={addressForm.deliveryMemo ?? ''}
                      maxLength={500}
                      placeholder="선택 입력"
                      onChange={(event) => updateAddressField('deliveryMemo', event.target.value)}
                    />
                  </Field>
                  {isAddressInputComplete(addressForm) && getHappydayAddressValidationError(addressForm) ? (
                    <Alert variant="destructive">
                      <AlertTriangle />
                      <AlertTitle>해피데이 배송지 규격을 확인해 주세요.</AlertTitle>
                      <AlertDescription>{getHappydayAddressValidationError(addressForm)}</AlertDescription>
                    </Alert>
                  ) : null}
                  <div className="flex justify-end gap-2">
                    {addresses.length > 0 ? (
                      <Button
                        type="button"
                        variant="outline"
                        disabled={createAddressMutation.isPending}
                        onClick={() => setAddingAddress(false)}
                      >
                        취소
                      </Button>
                    ) : null}
                    <Button type="button" disabled={!canCreateAddress} onClick={createAddress}>
                      {createAddressMutation.isPending ? <Spinner data-icon="inline-start" /> : null}
                      배송지 저장
                    </Button>
                  </div>
                </FieldGroup>
              </CardContent>
            </Card>
          ) : null}
        </FieldGroup>

        <Alert>
          <HelpCircle />
          <AlertTitle>중복 출고 방지</AlertTitle>
          <AlertDescription>
            통신 오류가 발생하면 자동 재시도하지 않습니다. 해피데이 사이트에서 접수 여부를 먼저 확인해 주세요.
          </AlertDescription>
        </Alert>

        <DialogFooter>
          <Button type="button" variant="outline" disabled={isBusy} onClick={onClose}>
            취소
          </Button>
          <Button type="button" disabled={!dispatchReady} onClick={submitDispatch}>
            {dispatchMutation.isPending ? <Spinner data-icon="inline-start" /> : <Send data-icon="inline-start" />}
            센터 출고 요청 전송 ({selectedRequests.length}건)
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
